import useRequest from "@/utils/useReq";
import { useSafeState } from "ahooks";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { VIP_CONFIG } from "../../constants/vip";
import PayQRCode from "./PayQRCode";

import useAppStore from "@/context/store";
import { pay_action } from "@/tracker/collecter/pay";
import { gdSlsReportError } from "@/utils/gdSlsRum";
import qs from "qs";
import {
  getOrder,
  getOrderByCode,
  getOrderSubscriptionTicket,
  getOrderToken,
} from "../../services";
import styles from "./index.module.less";

/**
 * 支付组件
 * 功能： 支付二维码显示，自动轮询查询订单变化， 根据订单响应相应的事件；
 * @param {Object} props
 * @param {Number} props.order_no 订单编号 用于继续支付场景使用
 * @param {Number} props.user_id 用户ID 用于继续支付场景使用
 * @param {{
 *  skus: [{sku_id: number,quantity:number,is_upgrade: boolean}],
 *  scene: "normal"|"upgrade"|"addition"
 * }} props.skuParam 商品信息 用于新建/重建订单场景使用
 * @param {Function} props.onDone 订单完成
 * @param {Function} props.onPaid 订单支付
 * @param {Function} props.onFaild 订单失败
 * @param {Function} props.onClose 订单关闭
 * @param {Function} props.onOrderChange
 * @param {import("react").CSSProperties} props.style 样式
 * @param {Number | String} props.padding 边距
 * @param {Number} props.size 二维码大小 不包括边距
 * @param {Boolean} props.disabledFn 失效监测功能
 * @returns
 */
function RawPay(
  {
    order_no,
    user_id,
    skuParam,
    onDone,
    onPaid,
    onFaild,
    onOrderChange,
    onClose,
    onRefresh,
    disabledFn = true,
    imageSettings = {},
    ...props
  },
  ref
) {
  const [qrcodeDisabled, setQrcodeDisabled] = useSafeState(true);
  const [disabledText, setDisabledText] = useSafeState("生成中");
  const qrcodeRef = useRef();

  // 是否是自动续费
  const isAutoRenew = useMemo(() => {
    if (skuParam) {
      return skuParam.is_auto_renew;
    }
  }, [skuParam]);

  // 订单信息，用于重新生成二维码使用与前后订单状态对比使用
  const oldOrder = useRef(null);

  const pollingOrderTimer = useRef(null);
  const hasPaid = useRef(false);

  // token过期时间 秒
  const [tokenExpireTime, setTokenExpireTime] = useSafeState();

  // 套餐变化，二维码更新，清除已生成的订单信息
  useEffect(() => {
    if (skuParam) {
      oldOrder.current = null;
    }
  }, [skuParam]);

  const params = useRequest(
    async () => {
      // 二维码刷新时，二维码置灰状态取消
      setQrcodeDisabled(false);
      setDisabledText("重新获取");

      if (order_no || oldOrder.current) {
        return {
          user_id: user_id || oldOrder.current.user_id,
          order_no: order_no || oldOrder.current.order_no,
        };
      }

      if (skuParam) {
        if (isAutoRenew) {
          const data = await getOrderSubscriptionTicket({
            sku: skuParam.skus[0],
            ...(skuParam.promotion || {}),
          });

          // token过期时间 秒
          setTokenExpireTime(data.expires);
          return data;
        }

        const data = await getOrderToken(skuParam);

        if (data.err) {
          setQrcodeDisabled(true);
          setDisabledText("重新获取");
          return false;
        }

        // token过期时间 秒
        setTokenExpireTime(data.expires);

        return data;
      }

      console.error("缺少参数");
      return false;
    },
    {
      refreshDeps: [order_no, user_id, skuParam, isAutoRenew],
    }
  );

  const wrapOnRefresh = useCallback(() => {
    // 当有订单时,则根据订单编号生成二维码,且开启订单倒计时
    // if (oldOrder.current !== null) {
    //   return;
    // }
    // 当前未过期状态不支持刷新
    if (!qrcodeDisabled) return;
    params.refresh();
    setQrcodeDisabled(false);
    onRefresh && onRefresh(oldOrder.current);
  }, [onRefresh, params, qrcodeDisabled, setQrcodeDisabled]);

  const pollingOrder = useCallback(() => {
    pollingOrderTimer.current = setTimeout(async () => {
      let order;
      // 当有订单或者扫码后生成的订单时，根据订单编号轮询
      if (order_no || oldOrder.current) {
        order = await getOrder(order_no || oldOrder.current.order_no);
      } else {
        if (params?.data) {
          order = await getOrderByCode(
            params.data.token || params.data.order_token
          );
        }
      }

      // 检测订单状态是否变化current
      if (order?.status !== oldOrder.current?.status) {
        onOrderChange && onOrderChange(order, oldOrder.current);
        oldOrder.current = order;

        // 创建订单后，二维码过期
        if (skuParam && order.status >= 0) {
          // 支付二维码过期
          setQrcodeDisabled(true);
          setDisabledText("扫码成功");

          // 订单生成时上报埋点
          order.status === 0 &&
            pay_action(qrcodeRef.current, {
              action_type: "订单生成",
              order_no: order.order_no,
              sku: order.skus.map((sku) => sku.ref_body.name),
              pay_amount: order.amount,
              payment_type: "扫码支付",
            });
        }
      }

      if (order?.status === 2) {
        if (order_no) {
          // 继续支付订单情况下订单取消
          setQrcodeDisabled(true);
          onClose && onClose(order);
          setDisabledText(
            <>
              <div>二维码失效</div>
              <div>订单已取消</div>
            </>
          );
        } else {
          // 会员支付页订单创建过期自动取消
          setQrcodeDisabled(true);
          setDisabledText("重新获取");
          oldOrder.current = null;
        }
        return;
      }

      // 未生成订单、未付款或验证付款中或已付款或发送虚拟商品中
      if (
        !order ||
        order.err === 204 ||
        order.status < 7 ||
        order.status === 8
      ) {
        pollingOrder();
      }

      // 支付成功
      if (order?.status >= 5 && order?.status <= 8 && !hasPaid.current) {
        pay_action(qrcodeRef.current, {
          action_type: "订单支付",
          order_no: order.order_no,
          sku: order.skus.map((sku) => sku.ref_body.name),
          pay_amount: order.amount,
          payment_type: "扫码支付",
        });
        setQrcodeDisabled(true);
        onPaid && onPaid(order);
        hasPaid.current = true;
      }

      // 支付且系统分发会员完成,订单完成
      if (order?.status === 7) {
        onDone && onDone(order);
      }

      // 验证付款出错（系统错误） 或者 发送虚拟商品失败
      if (order?.status === 4 || order?.status === 9) {
        setQrcodeDisabled(true);
        onFaild && onFaild(order);
      }
    }, 2000);
  }, [
    order_no,
    params.data,
    onOrderChange,
    skuParam,
    setQrcodeDisabled,
    setDisabledText,
    onClose,
    onPaid,
    onDone,
    onFaild,
  ]);

  // 订单轮询
  useEffect(() => {
    clearTimeout(pollingOrderTimer.current);
    pollingOrder();

    return () => {
      clearTimeout(pollingOrderTimer.current);
    };
  }, [pollingOrder]);

  // token过期定时器
  const tokenExpireTimer = useRef(null);
  const { refresh: refreshToken } = params;
  useEffect(() => {
    clearTimeout(tokenExpireTimer.current);
    if (skuParam && tokenExpireTime && oldOrder.current === null) {
      // 根据后端返回的token过期时间，设置定时器，过期自动刷新token
      tokenExpireTimer.current = setTimeout(() => {
        refreshToken();

        // 提前5秒刷新token，避免token过期
      }, 1000 * (tokenExpireTime - 5));

      return () => {
        clearTimeout(tokenExpireTimer.current);
      };
    }
  }, [params.data, refreshToken, skuParam, tokenExpireTime]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        oldOrder.current = null;
        params.refresh();
      },
    }),
    [params]
  );

  // 获取二维码参数错误日志上报
  if (params.error) {
    gdSlsReportError(params.error);
    // if (typeof window !== "undefined" && window._SLS_) {
    //   window._SLS_.reportError(params.error);
    // }
  }

  // 二维码内容
  const qrcodeValue = useMemo(() => {
    if (params.data) {
      let pageParams;
      if (isAutoRenew) {
        pageParams = {
          hb: params.data.user_id,
          t: params.data.ticket,
          gd: params.data.gd_id,
          // 支付页面灰度与手机号灰度一致
          canary_force: useAppStore.getState().envInfo.isCanary,
        };
      } else {
        pageParams = {
          ...params.data,
          // 支付页面灰度与手机号灰度一致
          canary_force: useAppStore.getState().envInfo.isCanary,
        };
      }

      return `${VIP_CONFIG.payUrl}/pay/transit.html?${qs.stringify(
        pageParams
      )}`;
    }
  }, [params.data, isAutoRenew]);

  return (
    <PayQRCode
      value={qrcodeValue}
      disabled={qrcodeDisabled && disabledFn}
      ref={qrcodeRef}
      imageSettings={imageSettings}
      disabledText={
        disabledText === "扫码成功" ? (
          <div className={styles.disabledText}>
            <div>扫码成功</div>
            <a onClick={wrapOnRefresh}>重新获取</a>
          </div>
        ) : disabledText === "重新获取" ? (
          <a onClick={wrapOnRefresh}>重新获取</a>
        ) : (
          disabledText
        )
      }
      // onClick={wrapOnRefresh}
      {...props}
    />
  );
}

const Pay = forwardRef(RawPay);
export default Pay;
